import CardPaymentData from '@components/card-payment-data/card-payment-data.component'
import InternationalPayment from '@components/international-payment/international-payment.component'
import ModalPaymentErrors from '@components/modals/modal-payment-errors/modal-payment-errors.component'
import ModalPaymentOnboarding from '@components/modals/modal-payment-onboarding/modal-payment-onboarding.component'
import { withTransaction } from '@elastic/apm-rum-react'
import {
  Bank,
  Button,
  DakenService,
  Loader,
  LogService,
  ModalUnsaveData,
  ObjectHelper,
  ProfileAdditionalInfo,
  TioPatinhasService,
  User,
  UserHelper,
  localForageHelper,
  onUserChange,
  useMessage,
} from '@hub/shared'
import PaymentPayloadHelper from '@pages/payments-data/helper/payment-payload.helper'
import ErrorMessage from '@src/enums/error-message.enum'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SubscriptionLike } from 'rxjs'
import PaymentContentPageComponent from './components/payment-content-page/payment-content-page.component'
import './payments-data.page.scoped.scss'
export interface CanEdit {
  bank: boolean
  full: boolean
  pis: boolean
}

interface BankResponse {
  id: string
  profileAdditionalInfo: ProfileAdditionalInfo
  profileAdditionalInfoBank: Bank
}

const PaymentDataPageComponent = () => {
  const paymentPayloadHelper = useMemo(() => new PaymentPayloadHelper(), [])
  const objectHelper = useMemo(() => new ObjectHelper(), [])
  const onUserChangeSubscription = useRef<SubscriptionLike | undefined>(undefined)
  const [urlCreators, setUrlCreators] = useState<string>('')
  const logService = useMemo(() => new LogService(), [])
  const dakenService = useMemo(() => new DakenService(), [])
  const userHelper = useMemo(() => new UserHelper(), [])

  const [t] = useTranslation('paymentsData')
  const { showMessage } = useMessage(ErrorMessage, 'payments')
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const [loadingButton, setLoadingButton] = useState<boolean>(false)
  const [openModalOnboarding, setOpenModalOnboarding] = useState<boolean>(false)
  const [openModalErrors, setOpenModalErrors] = useState<boolean>(false)
  const [internationalPayment, setInternationalPayment] = useState<boolean>(false)
  const [initialPayload, setInitialPayload] = useState<Bank | null>(null)
  const [payload, setPayload] = useState<Bank | null>(null)
  const [impersonateObject, setImpersonateObject] = useState<any>(null)
  const [hideData, setHideData] = useState(false)
  const [seeData, setSeeData] = useState(false)
  const tioService = useMemo(() => new TioPatinhasService(), [])
  const [errors, setErrors] = useState<any[]>([])
  const [loadingCanEdit, setLoadingCanEdit] = useState<boolean>(false)
  const [canEdit, setCanEdit] = useState<CanEdit | undefined>({
    bank: true,
    full: true,
    pis: true,
  })

  const [validSubmit, setValidSubmit] = useState({
    rpa: false,
    nf: false,
  })

  logService.init('Payments Page')

  const [infoChanged, setInfoChanged] = useState({
    state: false,
    message: '',
  })

  const getCantEdit = useCallback(async () => {
    try {
      if (user?.banks?.[0]?.id && !loadingCanEdit) {
        setLoadingCanEdit(true)
        const response = await tioService.canEditProfile()
        setCanEdit({
          bank: response?.bank,
          full: response?.full,
          pis: response?.pis,
        })
        setLoadingCanEdit(false)
      }
    } catch (error: any) {
      logService.error({ message: error?.message })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, tioService, logService])

  const submit = useCallback(
    async (paymentData: Bank | null) => {
      if (!loading && (!impersonateObject || impersonateObject?.impersonateAdmin)) {
        try {
          if (paymentData) {
            setLoadingButton(true)
            const sendPayload: Partial<Bank> = paymentPayloadHelper.getPayloadBank(paymentData, paymentData.paymentType)
            let response: BankResponse
            if (payload?.id) {
              response = await dakenService.patchUpdateBankInfo(paymentData?.id, sendPayload)
            } else {
              response = await dakenService.postAddBankInfo(sendPayload)
            }
            if ((response?.profileAdditionalInfoBank?.id || response.id) && user) {
              const newUser = user
              newUser.banks.pop()
              newUser.banks.push(response?.profileAdditionalInfoBank)
              setUser(newUser)
              await userHelper.setUser(user)
              showMessage({ message: t('success') }, 'success')
              setSeeData(false)
            } else {
              throw response
            }
          }
        } catch (error: any) {
          setLoadingButton(false)
          const errors: any = error?.data?.details?.validationErrors || []
          let errorList: any[] = []
          for (const aux in errors) {
            errorList.push(errors[aux]?.errorCode)
          }

          if (errorList?.length) {
            setErrors(errorList)
            setOpenModalErrors(true)
          } else {
            showMessage(error?.data, 'error')
          }
        } finally {
          setLoadingButton(false)
        }
      }
    },
    [t, loading, userHelper, impersonateObject, paymentPayloadHelper, user, payload, dakenService, showMessage]
  )

  const resetForm = useCallback(() => {
    setPayload(
      paymentPayloadHelper.toSetPayload(user?.banks?.[0], user?.profileadditionalinfo?.document, user?.profileadditionalinfo?.documentType)
    )
  }, [paymentPayloadHelper, user?.banks, user?.profileadditionalinfo?.document, user?.profileadditionalinfo?.documentType])

  const handleChangeModalError = useCallback(
    (value: boolean) => {
      setOpenModalErrors(value)
      if (!seeData) {
        resetForm()
      }
    },
    [resetForm, seeData]
  )

  useEffect(() => {
    onUserChangeSubscription.current = onUserChange.subscribe(async (userChanged: User) => {
      if (userChanged?.squidId) {
        setUser(userChanged)
        await getCantEdit()

        if (['International', 'RUT'].includes(userChanged?.profileadditionalinfo?.documentType)) {
          setInternationalPayment(true)
        } else if (!userChanged?.banks?.[0]) {
          setOpenModalOnboarding(true)
        }

        setInitialPayload(
          paymentPayloadHelper.toSetPayload(
            userChanged?.banks?.[0],
            userChanged?.profileadditionalinfo?.document,
            userChanged?.profileadditionalinfo?.documentType
          )
        )
        setPayload(
          paymentPayloadHelper.toSetPayload(
            userChanged?.banks?.[0],
            userChanged?.profileadditionalinfo?.document,
            userChanged?.profileadditionalinfo?.documentType
          )
        )
      }
    })

    return () => {
      onUserChangeSubscription?.current?.unsubscribe()
    }
  }, [getCantEdit, internationalPayment, paymentPayloadHelper])

  useEffect(() => {
    if (user?.squidId) {
      localForageHelper.getItem('impersonateUser').then((impersonateUser: any) => {
        if (impersonateUser) {
          setImpersonateObject(impersonateUser)
          setHideData(!!impersonateUser && !impersonateUser?.impersonateAdmin)
        }
      })
    }
  }, [user, initialPayload?.id])

  useEffect(() => {
    setInfoChanged((prevState) => ({
      ...prevState,
      state: !objectHelper.compareObjects(initialPayload, payload) && !openModalErrors,
      message: t('unsavedDataMessage'),
    }))
  }, [objectHelper, initialPayload, payload, t, openModalErrors])

  if (loading || loadingCanEdit) {
    return (
      <div className="text-center my-5" style={{ width: '100%' }}>
        <Loader size="big" />
      </div>
    )
  }

  let closePage = null
  if (seeData) {
    closePage = (
      <Button onClick={() => setSeeData(false)} color="transparent" textColor="var(--text_color)" borderStyle="none" style={{ padding: 0 }}>
        <i className="fa-solid fa-xmark"></i>
      </Button>
    )
  }

  let pageContent = (
    <PaymentContentPageComponent
      payload={initialPayload}
      seeData={seeData}
      hideData={hideData}
      onSave={(value: Bank | null) => submit(value)}
      onSeeData={(value: boolean) => setSeeData(value)}
      canEdit={canEdit}
      loading={loadingButton}
    ></PaymentContentPageComponent>
  )
  if (!internationalPayment && payload?.id && !seeData) {
    pageContent = <CardPaymentData canEdit={!canEdit?.bank && !canEdit?.full} hideData={hideData} seeData={(value) => setSeeData(value)} />
  } else if (internationalPayment && !payload?.id) {
    pageContent = <InternationalPayment />
  }

  return (
    <div className="payment-data-page box mb-0">
      <h1 className="display-flex justify-content-space-between align-items-center">
        {t('pageTitle')} {closePage}
      </h1>
      {pageContent}
      <ModalPaymentOnboarding openModal={openModalOnboarding} setOpenModal={(value) => setOpenModalOnboarding(value)} />
      <ModalPaymentErrors errors={errors} openModal={openModalErrors} setOpenModal={(value) => handleChangeModalError(value)} />
      <ModalUnsaveData
        infoChanged={infoChanged}
        disableButton={loadingButton || hideData}
        loading={loadingButton}
        onDiscard={() => setInfoChanged({ ...infoChanged, state: false, message: '' })}
        onSave={() => submit(payload)}
      />
    </div>
  )
}

export default withTransaction('PaymentDataPageComponent', 'component')(PaymentDataPageComponent)
